import { useState } from 'preact/hooks';
import { getConfig } from '../config';
import {
  updateCartQuantity,
  formatApiErrorFromPayload,
  formatOrderLimitToast,
  applyCartResultToStore,
  invalidateCartGetCache,
} from '../api';
import { store } from '../store';
import { t } from '../i18n';

interface Props {
  productId: string;
  amount: number;
  maxQty: number;
  limitCode?: number;
  onUpdated?: (cart: Record<string, unknown>) => void;
  disabled?: boolean;
  compact?: boolean;
}

export function CartQuantityControl({
  productId,
  amount,
  maxQty,
  limitCode = 60102,
  onUpdated,
  disabled = false,
  compact = false,
}: Props) {
  const [busy, setBusy] = useState(false);
  const qty = Math.max(1, Number(amount) || 1);
  const max = Math.max(1, Number(maxQty) || 1);
  const atMin = qty <= 1;
  const atMax = qty >= max;

  const apply = async (next: number) => {
    if (busy || disabled || !productId) return;
    const clamped = Math.min(max, Math.max(1, next));
    if (clamped === qty) {
      if (next > max) {
        store.addToast(formatOrderLimitToast(limitCode, max), 'error');
      }
      return;
    }
    setBusy(true);
    const config = getConfig();
    const res = await updateCartQuantity(config, productId, clamped);
    setBusy(false);
    if (res.status === 200 && res.result) {
      invalidateCartGetCache(config);
      applyCartResultToStore(store, res.result as { products?: unknown[]; total?: unknown });
      onUpdated?.(res.result as Record<string, unknown>);
      return;
    }
    store.addToast(formatApiErrorFromPayload(res as unknown as Record<string, unknown>) || t('cartPage.updateQtyFailed'), 'error');
  };

  return (
    <div
      class={`sw-cart-qty-control input-group input-group-sm${compact ? ' sw-cart-qty-control--compact' : ''}`}
      style={{ maxWidth: compact ? '6.75rem' : '8.25rem', margin: compact ? undefined : '0 auto' }}
    >
      <button
        type="button"
        class="btn btn-outline-secondary px-2"
        disabled={disabled || busy || atMin}
        aria-label={t('cartPage.qtyDecrease')}
        onClick={() => void apply(qty - 1)}
      >
        −
      </button>
      <input
        type="text"
        inputMode="numeric"
        class="form-control text-center px-1"
        style={{ minWidth: '2.25rem' }}
        value={String(qty)}
        disabled={disabled || busy}
        onInput={(e) => {
          const el = e.target as HTMLInputElement;
          el.value = el.value.replace(/[^\d]/g, '');
        }}
        onBlur={(e) => {
          const raw = parseInt((e.target as HTMLInputElement).value, 10);
          if (!Number.isFinite(raw) || raw < 1) {
            (e.target as HTMLInputElement).value = String(qty);
            return;
          }
          void apply(raw);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            (e.target as HTMLInputElement).blur();
          }
        }}
      />
      <button
        type="button"
        class="btn btn-outline-secondary px-2"
        disabled={disabled || busy || atMax}
        aria-label={t('cartPage.qtyIncrease')}
        onClick={() => void apply(qty + 1)}
      >
        +
      </button>
    </div>
  );
}
